/**
 * CSDDD Graph Frontend Adapter — useCSDDDGraph hooks
 * Replaces hardcoded arrays with live Neo4j graph queries.
 * Shape mapping ensures existing rendering code works unchanged.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../services/api_service";

const BASE = "/graph/csddd";

async function gFetch(path) {
  try {
    return await apiFetch(`${BASE}${path}`);
  } catch {
    return null;
  }
}

/** Full command center data — single API call */
export function useCSDDDDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetch_ = useCallback(async () => {
    setLoading(true);
    const r = await gFetch("/dashboard");
    if (r && !r._error) { setData(r); setError(null); } else setError(r);
    setLoading(false);
  }, []);
  useEffect(() => { fetch_(); }, [fetch_]);
  return { data, loading, error, refetch: fetch_ };
}

/** Grievances as first-class entities */
export function useCSDDDGrievances() {
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch_ = useCallback(async () => {
    setLoading(true);
    const r = await gFetch("/grievances");
    if (r?.grievances) setGrievances(r.grievances.map(g => ({
      id: g.id, filed: g.filed || "", source: g.source || "", subject: g.subject || "",
      linkedImp: g.impact_id || "", status: g.status || "pending",
      assignee: g.assignee || "", updates: g.updates || 0,
      evIds: g.evIds || [], evidence: g.evidence || [], _source: "graph",
    })));
    setLoading(false);
  }, []);
  useEffect(() => { fetch_(); }, [fetch_]);
  return { grievances, loading, refetch: fetch_ };
}

/** Evidence docs with full metadata */
export function useCSDDDEvidence(verifiedOnly) {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch_ = useCallback(async () => {
    setLoading(true);
    const path = verifiedOnly ? "/evidence?verified=true" : "/evidence";
    const r = await gFetch(path);
    if (r?.evidence) setEvidence(r.evidence.map(ev => ({
      ...ev, linked_impacts: ev.linked_impacts || [], _source: "graph",
    })));
    setLoading(false);
  }, [verifiedOnly]);
  useEffect(() => { fetch_(); }, [fetch_]);
  return { evidence, loading, refetch: fetch_ };
}

/** Material scope topics from graph */
export function useCSDDDScope() {
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetch_ = useCallback(async () => {
    setLoading(true);
    const r = await gFetch("/scope");
    if (r && !r._error) setScope(r);
    setLoading(false);
  }, []);
  useEffect(() => { fetch_(); }, [fetch_]);
  return { scope, loading, refetch: fetch_ };
}

/** Write: create impact */
export async function createImpactGraph(sid, data) {
  try {
    const { apiFetch } = await import("../services/api_service");
    return apiFetch(`${BASE}/impacts`, { method: "POST", body: { sid, ...data } });
  } catch { return null; }
}

/** Write: update impact */
export async function updateImpactGraph(iid, data) {
  try {
    const { apiFetch } = await import("../services/api_service");
    return apiFetch(`${BASE}/impacts/${iid}`, { method: "PUT", body: data });
  } catch { return null; }
}

/** One-time seed */
export async function seedCSDDDGraph() {
  try {
    const { apiFetch } = await import("../services/api_service");
    return apiFetch(`${BASE}/seed`, { method: "POST" });
  } catch { return null; }
}
